import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { toast } from 'sonner';

export interface AutomationJob {
  id: string;
  job_name: string;
  base_prompt: string;
  prompt_variations: any;
  models_to_use: any;
  total_images_target: number;
  images_generated: number;
  size: string;
  quality: string;
  style: string;
  n: number;
  status: string;
  start_time: string | null;
  end_time: string | null;
  pause_time: string | null;
  rpm_limit: number;
  requests_made_this_minute: number;
  last_request_time: string | null;
  success_count: number;
  error_count: number;
  last_error: string | null;
  retry_count: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  selected_api_key_id: string | null;
}

export const useAutomationJobs = () => {
  const { user } = useSecureAuthContext();
  const [jobs, setJobs] = useState<AutomationJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<Record<string, boolean>>({});
  const rateLimitUntilRef = useRef<Map<string, number>>(new Map());

  const fetchJobs = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('automated_generation_jobs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load automation jobs');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchJobs();

    // Set up realtime subscription
    const channel = supabase
      .channel('automation-jobs-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'automated_generation_jobs'
        },
        (payload) => {
          console.log('Job update:', payload);
          fetchJobs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchJobs]);

  const createJob = async (jobData: {
    job_name: string;
    base_prompt: string;
    prompt_variations?: string[];
    models_to_use: string[];
    total_images_target: number;
    size?: string;
    quality?: string;
    style?: string;
    n?: number;
    rpm_limit?: number;
    selected_api_key_id?: string | null;
  }, showToast: boolean = true) => {
    if (!user?.id) {
      if (showToast) toast.error('User not authenticated');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('automated_generation_jobs')
        .insert({
          ...jobData,
          created_by: user.id,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      if (showToast) toast.success('Automation job created successfully');
      fetchJobs();
      return data;
    } catch (error) {
      console.error('Error creating job:', error);
      if (showToast) toast.error('Failed to create automation job');
      return null;
    }
  };

  // Create multiple jobs from bulk prompts
  const createBulkJobs = async (
    prompts: { number: number; title: string; prompt: string }[],
    settings: {
      models_to_use: string[];
      size: string;
      quality: string;
      style: string;
      images_per_prompt: number;
    }
  ) => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return [];
    }

    const createdJobs: AutomationJob[] = [];
    const toastId = toast.loading(`Creating ${prompts.length} jobs...`);

    try {
      for (let i = 0; i < prompts.length; i++) {
        const prompt = prompts[i];
        const jobName = prompt.title; // Use title only, no number prefix
        
        const job = await createJob({
          job_name: jobName,
          base_prompt: prompt.prompt,
          models_to_use: settings.models_to_use,
          total_images_target: settings.images_per_prompt,
          size: settings.size,
          quality: settings.quality,
          style: settings.style,
          rpm_limit: 5
        }, false); // Don't show individual toasts

        if (job) {
          createdJobs.push(job);
        }
        
        // Update progress
        if ((i + 1) % 5 === 0 || i === prompts.length - 1) {
          toast.loading(`Created ${i + 1}/${prompts.length} jobs...`, { id: toastId });
        }
      }

      toast.success(`Created ${createdJobs.length} automation jobs`, { id: toastId });
      await fetchJobs();
      return createdJobs;
    } catch (error) {
      console.error('Error creating bulk jobs:', error);
      toast.error('Failed to create some jobs', { id: toastId });
      return createdJobs;
    }
  };

  // Start all pending jobs
  const startAllPending = async () => {
    const pendingJobs = jobs.filter(j => j.status === 'pending');
    if (pendingJobs.length === 0) {
      toast.error('No pending jobs to start');
      return;
    }

    const toastId = toast.loading(`Starting ${pendingJobs.length} jobs...`);
    
    try {
      for (const job of pendingJobs) {
        await supabase
          .from('automated_generation_jobs')
          .update({
            status: 'running',
            start_time: new Date().toISOString(),
            pause_time: null
          })
          .eq('id', job.id);
      }

      // Trigger processing
      await supabase.functions.invoke('process-automation-job', { body: {} });
      
      toast.success(`Started ${pendingJobs.length} jobs`, { id: toastId });
      await fetchJobs();
    } catch (error) {
      console.error('Error starting jobs:', error);
      toast.error('Failed to start some jobs', { id: toastId });
    }
  };

  // Pause all running jobs
  const pauseAllRunning = async () => {
    const runningJobs = jobs.filter(j => j.status === 'running');
    if (runningJobs.length === 0) {
      toast.error('No running jobs to pause');
      return;
    }

    const toastId = toast.loading(`Pausing ${runningJobs.length} jobs...`);
    
    try {
      for (const job of runningJobs) {
        await supabase
          .from('automated_generation_jobs')
          .update({
            status: 'paused',
            pause_time: new Date().toISOString()
          })
          .eq('id', job.id);
      }

      toast.success(`Paused ${runningJobs.length} jobs`, { id: toastId });
      await fetchJobs();
    } catch (error) {
      console.error('Error pausing jobs:', error);
      toast.error('Failed to pause some jobs', { id: toastId });
    }
  };

  const startJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .update({
          status: 'running',
          start_time: new Date().toISOString(),
          pause_time: null // Clear pause time when resuming
        })
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job started');
      
      // Immediately trigger processing by calling the edge function
      try {
        await supabase.functions.invoke('process-automation-job', {
          body: { jobId }
        });
        console.log('✅ Triggered immediate processing for job:', jobId);
      } catch (invokeError) {
        console.log('⚠️ Could not immediately trigger processing:', invokeError);
      }
      
      fetchJobs();
    } catch (error) {
      console.error('Error starting job:', error);
      toast.error('Failed to start job');
    }
  };


  const pauseJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .update({
          status: 'paused',
          pause_time: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job paused');
      fetchJobs();
      setProcessing(prev => ({ ...prev, [jobId]: false }));
    } catch (error) {
      console.error('Error pausing job:', error);
      toast.error('Failed to pause job');
    }
  };

  const stopJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .update({
          status: 'stopped',
          end_time: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job stopped');
      fetchJobs();
      setProcessing(prev => ({ ...prev, [jobId]: false }));
    } catch (error) {
      console.error('Error stopping job:', error);
      toast.error('Failed to stop job');
    }
  };

  const deleteJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .delete()
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job deleted');
      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  };

  return {
    jobs,
    loading,
    processing,
    createJob,
    createBulkJobs,
    startJob,
    startAllPending,
    pauseJob,
    pauseAllRunning,
    stopJob,
    deleteJob,
    refreshJobs: fetchJobs
  };
};
